import React from 'react';
import { InitiateAIChatButton, MatchActions } from '../components/matches';
import {
  MATCH_FEEDBACK,
  MATCH_STATUS,
  type Match,
  type StatusColor,
} from '../types/match';
import AgentReviewMatchActions from '../components/matches/AgentReviewMatchActions';

export const getStatusColor = (status: string): StatusColor => {
  switch (status.toLowerCase()) {
    case MATCH_STATUS.PENDING:
      return 'gray';
    case MATCH_STATUS.AWAITING_OTHER:
      return 'warning';
    case MATCH_STATUS.APPROVED:
      return 'success';
    case MATCH_STATUS.PASSED_BY_ME:
      return 'error';
    case MATCH_STATUS.PASSED_BY_OTHER:
      return 'error';
    case MATCH_STATUS.PASSED:
      return 'error';
    case 'pursued':
      return 'success';
    default:
      return 'error';
  }
};

export const getStatusText = (status: string): string => {
  switch (status.toLowerCase()) {
    case MATCH_STATUS.PENDING:
      // Jun-16 (a user V1 test, D1): "Pending your review" read as if the other
      // person had sent an inbound request. These are algorithmic suggestions,
      // so frame them as such.
      return 'Suggested for you';
    case MATCH_STATUS.AWAITING_OTHER:
      // Apr-25: was "Awaiting other" — ambiguous about WHO is waiting
      // for whom. New label makes it explicit: you've decided, they haven't.
      return 'Waiting on them';
    case MATCH_STATUS.APPROVED:
      return 'Mutual approval';
    case MATCH_STATUS.REJECTED:
      return 'Passed';
    case MATCH_STATUS.PASSED_BY_ME:
      return 'You passed';
    case MATCH_STATUS.PASSED_BY_OTHER:
      return 'They passed';
    case MATCH_STATUS.PASSED:
      return 'Passed (both)';
    case MATCH_STATUS.PURSUED:
      return 'Pursued';
    case MATCH_STATUS.NOT_PURSUED:
      return 'Passed';
    default:
      return status;
  }
};

export const getStatusHandler = (
  match: Match,
  variant: 'horizontal' | 'vertical',
  onApprove?: () => Promise<void>,
  onReject?: (open: boolean) => Promise<void>,
  openReviewModal?: boolean,
  setOpenReviewModal?: (open: boolean) => void
): React.ReactNode => {
  const { status_label, feedback } = match;

  if (status_label.toLowerCase() === MATCH_STATUS.PENDING) {
    return (
      <>
        {variant === 'vertical' && <hr className='border-gray-200 my-4' />}
        <MatchActions
          match={match}
          onApprove={onApprove}
          onReject={onReject}
          variant={variant}
          openReviewModal={openReviewModal}
          setOpenReviewModal={setOpenReviewModal}
        />
      </>
    );
  }
  if (status_label.toLowerCase() === MATCH_STATUS.AWAITING_OTHER) {
    // Apr-25: rewrote copy. Was "Awaiting matched approval to initiate
    // AI chat for this match" — used legacy "AI chat" terminology and was
    // ambiguous about who's waiting on whom. New copy explicitly tells the
    // user they've already approved and what unblocks the conversation.
    return (
      <>
        {variant === 'vertical' && <hr className='border-gray-200 my-4' />}
        <div
          className={`font-medium text-body-700 my-1 ${variant === 'horizontal' ? 'text-base' : 'text-sm'}`}
        >
          You approved. Waiting for them to approve before you can message.
        </div>
      </>
    );
  }
  if (status_label.toLowerCase() === MATCH_STATUS.APPROVED) {
    // Apr-25: replaced the static "AI chat initiated for this match"
    // text with a usable button so the user can re-enter the conversation
    // from the match card without hunting through the chat list. Same
    // InitiateAIChatButton component — startDirectMessage() returns the
    // existing channel id idempotently when one already exists.
    return (
      <>
        {variant === 'vertical' && <hr className='border-gray-200 my-4' />}
        <InitiateAIChatButton
          match={match}
          variant={variant}
          alreadyInitiated={Boolean(match?.is_ai_chat_initiated)}
        />
      </>
    );
  }
  if (
    status_label.toLowerCase() === MATCH_STATUS.REJECTED ||
    status_label.toLowerCase() === MATCH_STATUS.PASSED ||
    status_label.toLowerCase() === MATCH_STATUS.PASSED_BY_ME ||
    status_label.toLowerCase() === MATCH_STATUS.PASSED_BY_OTHER
  ) {
    return (
      <>
        {variant === 'vertical' && <hr className='border-gray-200 my-4' />}
        <div
          className={`font-medium my-1 ${feedback === MATCH_FEEDBACK ? 'text-green-700' : 'text-red-600'} ${variant === 'horizontal' ? 'text-base' : 'text-sm'}`}
        >
          {feedback ? feedback : 'No feedback was given'}
        </div>
      </>
    );
  }
  if (
    match.status_label === MATCH_STATUS.PURSUED ||
    match.status_label === MATCH_STATUS.NOT_PURSUED
  ) {
    return (
      <>
        {variant === 'vertical' && <hr className='border-gray-200 my-4' />}
        <AgentReviewMatchActions match={match} variant={variant} />
      </>
    );
  }
};

export const getCurrentUsersCount = (
  page: number,
  limit: number,
  total: number
) => {
  // Calculate how many users would have been seen up to this page
  const expectedCount = page * limit;

  // Return the smaller of the two to avoid exceeding total users
  return Math.min(expectedCount, total);
};
